/**
 * Máquina de estados de Proposta.
 *
 * Define quais transições de status são permitidas. A regra é única — fonte
 * de verdade tanto pra UI (esconder/desabilitar botões) quanto pra API
 * (validar requests).
 *
 * Convenção: sempre que o config global `REQUIRE_APPROVAL_ALL=true`, toda
 * proposta passa pelo estado intermediário `aguardando-aprovacao` antes de
 * sair do rascunho. Esse estado não está no enum atual de `StatusProposta`;
 * vamos introduzi-lo numa próxima iteração junto com o admin toggle.
 */

import type { StatusProposta } from './types/proposta.js';

/**
 * Mapa transições válidas: `de` → lista de `para` permitidos.
 * Status finais (concluida, perdida, cancelada) não têm saída.
 */
export const STATUS_TRANSITIONS: Record<StatusProposta, StatusProposta[]> = {
  rascunho: ['enviada', 'cancelada'],
  enviada: ['visualizada', 'aceita', 'perdida', 'cancelada'],
  visualizada: ['aceita', 'perdida', 'cancelada'],
  aceita: ['aguardando-dados-contrato', 'cancelada'],
  'aguardando-dados-contrato': ['contrato-gerado', 'cancelada'],
  'contrato-gerado': ['aguardando-assinatura', 'cancelada'],
  'aguardando-assinatura': ['assinada', 'cancelada'],
  assinada: ['implantacao', 'cancelada'],
  implantacao: ['concluida', 'cancelada'],
  concluida: [],
  perdida: [],
  cancelada: [],
};

/** Status finais — proposta não pode mais ser modificada */
export const STATUS_FINAIS: StatusProposta[] = ['concluida', 'perdida', 'cancelada'];

/** Status editáveis — campos da proposta podem ser alterados */
export const STATUS_EDITAVEIS: StatusProposta[] = ['rascunho'];

/** Verifica se transição é válida */
export function canTransition(from: StatusProposta, to: StatusProposta): boolean {
  return STATUS_TRANSITIONS[from].includes(to);
}

/** Lista de status que a proposta pode assumir a partir do atual */
export function nextStatuses(from: StatusProposta): StatusProposta[] {
  return STATUS_TRANSITIONS[from];
}

/** True se proposta está em estado final (imutável) */
export function isFinalStatus(s: StatusProposta): boolean {
  return STATUS_FINAIS.includes(s);
}

/** True se campos da proposta podem ser editados nesse status */
export function isEditableStatus(s: StatusProposta): boolean {
  return STATUS_EDITAVEIS.includes(s);
}
